import styled, { css } from 'styled-components'
import { SortableVestingScheduleItem } from '../../../../types/reducerTypes'
import { Box } from '../../../../shared_components/UI/Box'
import { wrapEthOrUsdb } from '../../../Home/useUpcomingProjects'
import { useState } from 'react'

function VestingSchedule({
  vestingSchedule,
  isEthRaise,
}: {
  vestingSchedule: SortableVestingScheduleItem[]
  isEthRaise: boolean
}) {
  const [sortedSchedule, setSortedSchedule] =
    useState<SortableVestingScheduleItem[]>(vestingSchedule)
  const [sortingByKey, setSortingByKey] = useState('')
  const [isDescending, setIsDescending] = useState(true)

  const DESIRED_COLUMNS = [
    {
      name: 'Allocation',
      width: 2,
      totalFormatter: () => 'Total',
      notSortable: true,
      isBold: true,
      type: 'string',
    },
    { name: 'Allocation %', totalFormatter: addPercentageSign },
    {
      name: 'Amount',
      cellFormatter: addMagnitudeLettersToNumber,
      totalFormatter: addMagnitudeLettersToNumber,
    },
    {
      name: 'Price',
      cellFormatter: (v: number) => wrapEthOrUsdb(v, isEthRaise),
      totalFormatter: () => '',
    },
    {
      name: 'Raise',
      cellFormatter: (v: number) =>
        addMagnitudeLettersToNumber(v, isEthRaise ? 'ETH' : 'USDB'),
      totalFormatter: (v: number) =>
        addMagnitudeLettersToNumber(v, isEthRaise ? 'ETH' : 'USDB'),
    },
    { name: 'TGE %' },
    {
      name: 'Vesting',
      width: 2,
      notSortable: true,
      type: 'string',
      totalFormatter: () => '',
    },
  ]

  function sortScheduleByColumn(name: string) {
    const sorted = sortedSchedule.slice().sort((a, b) => {
      return isDescending ? b[name] - a[name] : a[name] - b[name]
    })
    setSortingByKey(name)
    setIsDescending(!isDescending)
    setSortedSchedule(sorted)
  }

  return (
    <Container g={32} fw>
      <table>
        <thead>
          <tr>
            {DESIRED_COLUMNS.map(
              ({ name, width, notSortable, type }, index) => {
                const isSortingByCurrentColumn = sortingByKey === name
                return (
                  <TH
                    key={index}
                    colSpan={width ?? 1}
                    align={type === 'string' ? 'left' : 'center'}
                    isSortable={!notSortable}
                    onClick={
                      notSortable ? undefined : () => sortScheduleByColumn(name)
                    }
                    isDoubleWide={width === 2}
                  >
                    {name}
                    {notSortable ? (
                      <></>
                    ) : isSortingByCurrentColumn ? (
                      !isDescending ? (
                        <span> &#9660;</span>
                      ) : (
                        <span> &#9650;</span>
                      )
                    ) : (
                      <></>
                    )}
                  </TH>
                )
              },
            )}
          </tr>
        </thead>
        <tbody>
          {sortedSchedule.map((schedule, index) => (
            <tr key={index}>
              {DESIRED_COLUMNS.map(
                ({ name, width, isBold, type, cellFormatter }, index) => {
                  const value = schedule[name]
                  return (
                    <TD
                      key={index}
                      colSpan={width ?? 1}
                      align={type === 'string' ? 'left' : 'center'}
                      isBold={isBold}
                    >
                      {!value
                        ? '-'
                        : cellFormatter
                        ? cellFormatter(value)
                        : value}
                    </TD>
                  )
                },
              )}
            </tr>
          ))}
          <tr>
            {DESIRED_COLUMNS.map(({ totalFormatter, width, type, name }) => {
              const total = vestingSchedule.reduce(
                (acc, schedule) => acc + (schedule[name] ?? 0),
                0,
              )

              return (
                <TD
                  key={total}
                  colSpan={width ?? 1}
                  align={type === 'string' ? 'left' : 'center'}
                  isTotal
                >
                  {totalFormatter ? totalFormatter(total) : total}
                </TD>
              )
            })}
          </tr>
        </tbody>
      </table>
    </Container>
  )
}

type TDProps = {
  align: string
  isTotal?: boolean
  isBold?: boolean
  isSortable?: boolean
  isDoubleWide?: boolean
}

const TH = styled.th<TDProps>`
  text-align: ${({ align }) => align};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 100% */
  text-transform: uppercase;
  ${({ isSortable }) =>
    isSortable &&
    css`
      cursor: pointer !important;
      * {
        cursor: pointer !important;
      }
    `}

  @media screen and (max-width: 1400px) {
    min-width: ${({ isDoubleWide }) => (isDoubleWide ? 240 : 160)}px;
  }
`

const TD = styled.td<TDProps>`
  text-align: ${({ align }) => align};
  height: 88px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: 0.48px;
  min-width: 120px;
  ${({ isTotal }) =>
    isTotal &&
    css`
      font-weight: bold;
      border-bottom: none;
      background: rgba(255, 255, 255, 0.04);
    `}
  ${({ isBold }) =>
    isBold &&
    css`
      font-weight: 700;
    `}
`

const Container = styled(Box)`
  padding-top: 2rem;
  width: 100%;
  thead {
    width: 100%;
    height: 30px;
  }
  table {
    width: 100%;
    td,
    th {
      padding: 0 0.5rem;
    }
  }
  @media screen and (max-width: 1400px) {
    overflow-x: auto;
  }
`

export default VestingSchedule

function addPercentageSign(value: number) {
  return `${value}%`
}

function addMagnitudeLettersToNumber(value: number, currency?: string) {
  // for thousand give k, for million give m, for billion give b
  const wrapper = currency
    ? (v: string) =>
        `${currency === 'USDB' ? '$' : ''}${v.toUpperCase()} ${
          currency !== 'USDB' ? currency : ''
        }`
    : (v: string) => v.toUpperCase()
  const magnitude = Math.floor(Math.log10(value))
  if (magnitude < 3) return wrapper(String(value))
  if (magnitude < 6) return wrapper(`${(value / 1000).toFixed(2)}k`)
  if (magnitude < 9) return wrapper(`${(value / 1000000).toFixed(2)}m`)
  return wrapper(`${(value / 1000000000).toFixed(2)}b`)
}
